import { registerPageInit } from '../barba/page-init-registry';
import '../../css/_root.scss';
import '../../css/pages/personal-area/personal-area.scss';
import '../../css/pages/personal-area/general-information.scss';
import '../../css/pages/personal-area/settings.scss';
import '../../css/pages/personal-area/report/index.scss';
import '../../css/pages/personal-area/notification/index.scss';
import { TabWidget } from '../widgets/tab-widget';
import { FormWidget } from '../widgets/form-widget';
import { ImagePreviewWidget } from '../widgets/image-preview-widget';
import { getApiUrl } from '@/js/utils/getApiUrl';
import { getBitrixSessid } from '../utils/getBitrixSessid';
import {
  showStcErrorNotification,
  showStcSuccessNotification,
} from '@/js/components/stc-notification';
import { initResidentReports } from '../widgets/resident-reports';
import {
  initResidentReportDetail,
  getReportIdFromUrl,
  navigateToReportList,
  RESIDENT_LK_REPORTS_TAB_ID,
} from '../widgets/resident-report-detail';
import { initResidentReportDrawer } from '../widgets/resident-report-drawer';
import { initResidentNotifications } from '../widgets/resident-notifications';
import { initCompanyEditingDrawer } from '../widgets/company-editing-drawer';
import { initPasswordChangeModal } from '../widgets/password-change-modal';
import { initLogoutConfirmModal } from '../widgets/logout-confirm-modal';
import { initLkMobileNotice } from '../widgets/lk-mobile-notice';
import { initNotificationsSidebarBadge } from '../utils/notifications-sidebar-badge';
import { initResidentNotificationDrawer } from '../widgets/resident-notification-drawer';

class PersonalArea {
  private static readonly SETTINGS_FORM_SELECTOR = '#residentSettingsForm';
  private static readonly SETTINGS_FORM_BOUND_ATTR = 'residentSettingsFormBound';
  private lkTabWidget: TabWidget;
  private personalDataFormWidget: FormWidget;
  private readonly settingsSubmitUrl: string;
  private isSettingsSubmitting: boolean = false;

  constructor() {
    this.lkTabWidget = new TabWidget({
      rootSelector: '.personal-area__box',
    });

    const successMessageEl = document.createElement('div');
    const errorMessageEl = document.createElement('div');
    successMessageEl.innerHTML =
      "<p class='toast-template__title'>Данные сохранены</p><p class='toast-template__description'>данные успешно сохранены</p>";
    errorMessageEl.innerHTML =
      "<p class='toast-template__title'>Что-то пошло не так</p><p class='toast-template__description'>Пожалуйста, попробуйте еще раз</p>";

    this.personalDataFormWidget = new FormWidget({
      formSelector: '[id^="personalDataForm"]',
      submitUrl: getApiUrl('/api/personalDataForm.php'),
      successMessage: successMessageEl,
      errorMessage: errorMessageEl,
      noResetAfterSubmit: true,
    });

    this.settingsSubmitUrl = getApiUrl('/api/form/settings/');

    new ImagePreviewWidget({
      inputSelector: '#imageInput',
      previewWrapperSelector: '#imagePreviewWrapper',
      previewImgSelector: '#imagePreview',
      removeBtnSelector: '#removeImageBtn',
      errorSelector: '#imageError',
      maxSize: 3 * 1024 * 1024,
    });
  }

  public init(root?: ParentNode): void {
    initCompanyEditingDrawer(root);
    initPasswordChangeModal(root);
    initLogoutConfirmModal(root);
    initLkMobileNotice(root);
    this.personalDataFormWidget.init();

    initResidentReports(root);
    initResidentReportDetail(root);
    initResidentReportDrawer(root);
    initResidentNotifications(root);
    initResidentNotificationDrawer(root);
    initNotificationsSidebarBadge(root);
    this.bindEvents();

    const queryTab = this.getTabIdFromUrl();
    const isReportDetailPage = getReportIdFromUrl() !== null;
    this.lkTabWidget.init(
      queryTab ?? (isReportDetailPage ? RESIDENT_LK_REPORTS_TAB_ID : undefined),
      this.setTabIdToUrl,
    );

    if (isReportDetailPage && queryTab === null) {
      this.setTabIdToUrl(RESIDENT_LK_REPORTS_TAB_ID);
    }
  }

  private bindEvents(): void {
    const settingsForm = document.querySelector<HTMLFormElement>(
      PersonalArea.SETTINGS_FORM_SELECTOR,
    );
    if (!settingsForm) {
      return;
    }

    if (settingsForm.dataset[PersonalArea.SETTINGS_FORM_BOUND_ATTR] === '1') {
      return;
    }

    const handleChange = async (event: Event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement) || target.type !== 'checkbox') {
        return;
      }

      await this.submitSettingsAsJson(settingsForm);
    };

    settingsForm.addEventListener('change', handleChange);
    settingsForm.dataset[PersonalArea.SETTINGS_FORM_BOUND_ATTR] = '1';
  }

  private getSettingsCheckboxValue(form: HTMLFormElement, names: string[]): boolean {
    for (const name of names) {
      const input = form.querySelector<HTMLInputElement>(`input[name="${name}"]`);
      if (input) {
        return input.checked;
      }
    }

    return false;
  }

  private async submitSettingsAsJson(form: HTMLFormElement): Promise<void> {
    if (this.isSettingsSubmitting) {
      return;
    }

    const sessid = getBitrixSessid();
    if (!sessid) {
      console.error('Не удалось получить сессию пользователя');
      return;
    }

    const notifyManagerMessage = this.getSettingsCheckboxValue(form, [
      'notify_manager_message',
      'notify_new_message',
    ]);
    const notifyStatusReport = this.getSettingsCheckboxValue(form, ['notify_status_report']);

    this.isSettingsSubmitting = true;
    try {
      const response = await fetch(this.settingsSubmitUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Bitrix-Csrf-Token': sessid,
        },
        body: JSON.stringify({
          notify_manager_message: notifyManagerMessage ? 1 : 0,
          notify_status_report: notifyStatusReport ? 1 : 0,
          sessid,
        }),
      });

      const result = (await response.json()) as {
        msg?: string;
        status?: 'success' | 'error';
      };

      if (!response.ok || result.status === 'error') {
        showStcErrorNotification(result.msg || 'Ошибка обработки');
        return;
      }

      showStcSuccessNotification(result.msg || 'Данные успешно сохранены');
    } catch {
      showStcErrorNotification('Ошибка соединения');
    } finally {
      this.isSettingsSubmitting = false;
    }
  }

  private getTabIdFromUrl(): number | null {
    const params = new URLSearchParams(window.location.search);
    const tabId = params.get('tab');
    return tabId ? Number(tabId) : null;
  }

  private setTabIdToUrl(tabId?: number): void {
    if (tabId === null || tabId === undefined) return;

    if (tabId === RESIDENT_LK_REPORTS_TAB_ID && getReportIdFromUrl() !== null) {
      navigateToReportList();

      return;
    }

    const params = new URLSearchParams(window.location.search);
    params.set('tab', String(tabId));

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({}, '', newUrl);
  }
}

registerPageInit('personal-area', (root?: ParentNode) => {
  const page = new PersonalArea();
  page.init(root);
});
